import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DIALOG_CONTENT_FOR_IN_DIALOG_SELECT,
  Input,
  Label,
  Switch,
  Textarea,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  listPermissions,
  listWorkosFeatureFlags,
  updateRole,
} from '../../services/api';
import type {
  PermissionResponse,
  RbacScope,
  RoleResponse,
} from '../../services/api/types.gen';

const FEATURE_FLAG_NONE_VALUE = '__none__';

type Props = {
  role: RoleResponse;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
};

const EditRoleDialog: React.FC<Props> = ({
  role,
  open,
  onClose,
  onUpdated,
}) => {
  const { showError, showSuccess, hasPermission } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
      hasPermission: state.hasPermission,
    }))
  );
  const canViewInternalRoles = hasPermission(
    'clinician-mgr:view-internal-roles'
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [featureFlagSlug, setFeatureFlagSlug] = useState<string>('');
  const [featureFlagOptions, setFeatureFlagOptions] = useState<string[]>([]);
  const [isLoadingFeatureFlags, setIsLoadingFeatureFlags] = useState(false);
  const [selectedPerms, setSelectedPerms] = useState<PermissionResponse[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionResponse[]>(
    []
  );
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [permSearch, setPermSearch] = useState('');
  const [permDropdownOpen, setPermDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const permDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setName(role.name);
    setDescription(role.description ?? '');
    setIsInternal(role.is_internal ?? false);
    setFeatureFlagSlug(role.feature_flag_slug ?? FEATURE_FLAG_NONE_VALUE);
    setSelectedPerms(role.permissions ?? []);
    setPermSearch('');
    setPermDropdownOpen(false);
  }, [open, role]);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setIsLoadingPermissions(true);
      try {
        // An agency role may also carry a group-scope permission
        // (chat:send-announcement on the admin roles, EMR-1876). The list must
        // show it, or it cannot be re-added here once it is removed.
        const scopes: RbacScope[] =
          role.scope === 'agency'
            ? ['agency', 'group']
            : [role.scope as RbacScope];
        const responses = await Promise.all(
          scopes.map(scope =>
            listPermissions({
              query: { limit: 500, scope },
              throwOnError: true,
            })
          )
        );
        setAllPermissions(responses.flatMap(res => res.data ?? []));
      } catch (error) {
        console.error('Failed to load permissions:', error);
        showError('Failed to Load Permissions', 'Unable to load permissions.');
      } finally {
        setIsLoadingPermissions(false);
      }
    };
    load();
  }, [open, role.scope, showError]);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setIsLoadingFeatureFlags(true);
      try {
        const res = await listWorkosFeatureFlags();
        if (!res.data) {
          console.error('Failed to load feature flags: empty response', res);
          showError(
            'Failed to Load Feature Flag List',
            'Unable to load feature flags. Please try again.'
          );
          setFeatureFlagOptions(
            role.feature_flag_slug ? [role.feature_flag_slug] : []
          );
          return;
        }
        const slugs = res.data;
        const currentSlug = role.feature_flag_slug;
        // Keep a currently assigned slug selectable even if WorkOS no longer
        // returns it (e.g. renamed/removed flag).
        setFeatureFlagOptions(
          currentSlug && !slugs.includes(currentSlug)
            ? [currentSlug, ...slugs]
            : slugs
        );
      } catch (error) {
        console.error('Failed to load feature flags:', error);
        showError(
          'Failed to Load Feature Flag List',
          'Unable to load feature flags. Please try again.'
        );
        setFeatureFlagOptions(
          role.feature_flag_slug ? [role.feature_flag_slug] : []
        );
      } finally {
        setIsLoadingFeatureFlags(false);
      }
    };
    load();
  }, [open, role.feature_flag_slug, showError]);

  const featureFlagSelectOptions = useMemo(() => {
    const sortedFlags = [...featureFlagOptions]
      .sort((a, b) => a.localeCompare(b))
      .map(slug => ({
        value: slug,
        label: slug,
      }));
    return [
      {
        value: FEATURE_FLAG_NONE_VALUE,
        label: 'None (visible to all)',
      },
      ...sortedFlags,
    ];
  }, [featureFlagOptions]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        permDropdownRef.current &&
        !permDropdownRef.current.contains(e.target as Node)
      ) {
        setPermDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredPerms = allPermissions.filter(
    p =>
      p.name.toLowerCase().includes(permSearch.toLowerCase()) ||
      p.slug.toLowerCase().includes(permSearch.toLowerCase())
  );

  const togglePermission = (perm: PermissionResponse) => {
    setSelectedPerms(prev =>
      prev.some(p => p.id === perm.id)
        ? prev.filter(p => p.id !== perm.id)
        : [...prev, perm]
    );
  };

  const removePermission = (permId: string) => {
    setSelectedPerms(prev => prev.filter(p => p.id !== permId));
  };

  // const isReadOnly = role.is_system;
  const isReadOnly = false; // TODO PEDRAM: it's read only if you are not a roger admin
  const isFormValid = name.trim() !== '' && description.trim() !== '';

  const handleUpdate = async () => {
    if (!isFormValid || isReadOnly) return;
    setIsSaving(true);
    try {
      await updateRole({
        path: { role_id: role.id },
        body: {
          name: name.trim(),
          description: description.trim(),
          is_internal: isInternal,
          feature_flag_slug:
            featureFlagSlug === FEATURE_FLAG_NONE_VALUE
              ? null
              : featureFlagSlug,
          permission_ids: selectedPerms.map(p => p.id),
        },
      });
      showSuccess(
        'Role Updated',
        `Role "${name.trim()}" was updated successfully.`
      );
      onUpdated();
      onClose();
    } catch (error) {
      console.error('Failed to update role:', error);
      showError(
        'Failed to Update Role',
        'Unable to update role. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent
        className={cn(
          'flex max-h-[90vh] max-w-lg flex-col',
          DIALOG_CONTENT_FOR_IN_DIALOG_SELECT
        )}
      >
        <DialogHeader className='shrink-0'>
          <DialogTitle>Edit Role</DialogTitle>
        </DialogHeader>

        {/* Fields + feature-flag select stay outside the permissions scroller
            so the in-dialog menu is not clipped (Option A / inDialog). */}
        <div className='shrink-0 space-y-5 py-2'>
          {isReadOnly && (
            <div className='rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground'>
              System roles are read-only and cannot be modified.
            </div>
          )}

          {/* Name */}
          <div className='space-y-1.5'>
            <Label htmlFor='edit-role-name'>Name</Label>
            <Input
              id='edit-role-name'
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={isReadOnly}
            />
          </div>

          {/* Slug, Scope, Type — read-only, inline */}
          <div className='flex items-center gap-4 text-sm'>
            <div className='flex items-center gap-1.5'>
              <span className='text-muted-foreground'>Slug</span>
              <span className='font-mono text-xs'>{role.slug}</span>
            </div>
            <div className='flex items-center gap-1.5'>
              <span className='text-muted-foreground'>Scope</span>
              <Badge variant='outline' className='capitalize'>
                {role.scope.toLowerCase()}
              </Badge>
            </div>
            <div className='flex items-center gap-1.5'>
              <span className='text-muted-foreground'>Type</span>
              {role.is_system ? (
                <Badge variant='secondary'>System</Badge>
              ) : (
                <Badge variant='outline'>Custom</Badge>
              )}
            </div>
          </div>

          {/* Description */}
          <div className='space-y-1.5'>
            <Label htmlFor='edit-role-description'>Description</Label>
            <Textarea
              id='edit-role-description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              disabled={isReadOnly}
            />
          </div>

          {/* Internal Role */}
          {canViewInternalRoles && (
            <div className='flex items-center justify-between'>
              <div className='space-y-0.5'>
                <Label htmlFor='edit-role-internal'>Internal Role</Label>
                <p className='text-xs text-muted-foreground'>
                  Only visible to external admins in invite and edit flows
                </p>
              </div>
              <Switch
                id='edit-role-internal'
                checked={isInternal}
                onCheckedChange={setIsInternal}
                disabled={isReadOnly}
              />
            </div>
          )}

          {/* Feature Flag */}
          <div className='space-y-1.5'>
            <Label htmlFor='edit-role-feature-flag'>Feature Flag</Label>
            <p className='text-xs text-muted-foreground'>
              When SHOW_UNASSIGNED_ROLES is enabled, users must have this
              feature flag to see this role. Leave empty for no restriction.
            </p>
            {isReadOnly ? (
              <div className='text-sm'>
                {featureFlagSlug === FEATURE_FLAG_NONE_VALUE ||
                !featureFlagSlug ? (
                  <span className='italic text-muted-foreground'>None</span>
                ) : (
                  featureFlagSlug
                )}
              </div>
            ) : (
              <VirtualizedFormSelect
                key={
                  open
                    ? `edit-role-feature-flag-${role.id}-${featureFlagOptions.length}`
                    : 'closed'
                }
                placeholder={
                  isLoadingFeatureFlags
                    ? 'Loading feature flags...'
                    : 'Search feature flags...'
                }
                options={featureFlagSelectOptions}
                value={featureFlagSlug}
                onChange={e =>
                  setFeatureFlagSlug(e.target.value || FEATURE_FLAG_NONE_VALUE)
                }
                disabled={isReadOnly || isLoadingFeatureFlags}
                isClearable={false}
                sortOptions={false}
                inDialog
              />
            )}
          </div>
        </div>

        {/* Permissions scroll independently on short viewports. */}
        <div className='min-h-0 flex-1 space-y-1.5 overflow-y-auto py-2'>
          <Label>Permissions</Label>
          {isReadOnly ? (
            <div className='flex flex-wrap gap-1.5 pt-1'>
              {selectedPerms.length === 0 ? (
                <span className='text-sm text-muted-foreground italic'>
                  None
                </span>
              ) : (
                selectedPerms.map(perm => (
                  <Badge key={perm.id} variant='secondary'>
                    {perm.name}
                  </Badge>
                ))
              )}
            </div>
          ) : (
            <div className='relative' ref={permDropdownRef}>
              <Button
                variant='outline'
                className='w-full justify-between font-normal'
                type='button'
                disabled={isLoadingPermissions}
                onClick={() => setPermDropdownOpen(o => !o)}
              >
                <span className='text-sm text-muted-foreground truncate'>
                  {isLoadingPermissions
                    ? 'Loading permissions...'
                    : selectedPerms.length === 0
                      ? 'Select permissions...'
                      : `${selectedPerms.length} permission${selectedPerms.length !== 1 ? 's' : ''} selected`}
                </span>
                <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
              </Button>
              {permDropdownOpen && !isLoadingPermissions && (
                <div className='absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md'>
                  <div className='p-2 border-b'>
                    <Input
                      placeholder='Search permissions...'
                      value={permSearch}
                      onChange={e => setPermSearch(e.target.value)}
                      className='h-8 text-sm'
                      autoFocus
                    />
                  </div>
                  <div className='max-h-48 overflow-y-auto p-1'>
                    {filteredPerms.length === 0 ? (
                      <p className='py-2 text-center text-xs text-muted-foreground'>
                        {allPermissions.length === 0
                          ? 'No permissions available.'
                          : 'No permissions found.'}
                      </p>
                    ) : (
                      filteredPerms.map(perm => (
                        <div
                          key={perm.id}
                          className='flex w-full cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent'
                          onClick={() => togglePermission(perm)}
                        >
                          <Checkbox
                            checked={selectedPerms.some(s => s.id === perm.id)}
                            id={`edit-role-perm-${perm.id}`}
                            onCheckedChange={() => togglePermission(perm)}
                            onClick={e => e.stopPropagation()}
                          />
                          <div className='min-w-0 flex-1'>
                            <span className='font-medium leading-tight'>
                              {perm.name}
                            </span>
                            <span className='block truncate text-xs text-muted-foreground'>
                              {perm.slug}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              {selectedPerms.length > 0 && (
                <div className='flex flex-wrap gap-1.5 pt-2'>
                  {selectedPerms.map(perm => (
                    <Badge
                      key={perm.id}
                      variant='secondary'
                      className='flex items-center gap-1 pr-1'
                    >
                      {perm.name}
                      <button
                        type='button'
                        onClick={() => removePermission(perm.id)}
                        className='ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5'
                        aria-label={`Remove ${perm.name}`}
                      >
                        <X className='h-3 w-3' />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className='shrink-0'>
          <Button variant='outline' onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          {!isReadOnly && (
            <Button
              variant='purple'
              onClick={handleUpdate}
              disabled={!isFormValid || isSaving}
            >
              {isSaving ? 'Saving...' : 'Update'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRoleDialog;
